import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronDown,
  ChevronUp,
  RefreshCw,
  AlertCircle,
  ShieldCheck,
  CheckCircle2,
  ChevronRight,
  Target,
  Sliders,
  TrendingDown,
  Zap,
  Link2,
} from 'lucide-react';
import api from '../services/api';
import { KpiMetricsResponse, KpiMetricItem } from '../types';
import { KpiDetailModal } from './KpiDetailModal';

interface ExecutiveKpiScorecardProps {
  variant?: 'standalone' | 'accordion';
}

export const ExecutiveKpiScorecard: React.FC<ExecutiveKpiScorecardProps> = ({
  variant = 'standalone',
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<KpiMetricsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedKpi, setSelectedKpi] = useState<KpiMetricItem | null>(null);

  const fetchKpis = useCallback(async () => {
    if (!api?.analytics?.getKpis) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.analytics.getKpis();
      setData(res);
    } catch (err: unknown) {
      console.warn('[ExecutiveKpiScorecard] Failed to fetch KPIs:', err);
      setError((err as Error).message || 'Failed to load executive KPI metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKpis();
  }, [fetchKpis]);

  const summary = data?.summary;
  const metrics = data?.metrics || [];

  const getMetricIcon = (id: string) => {
    switch (id) {
      case 'signal_recall':
        return <Target className="w-4 h-4 text-amber-600" />;
      case 'signal_precision':
        return <Sliders className="w-4 h-4 text-amber-600" />;
      case 'monitoring_time':
        return <TrendingDown className="w-4 h-4 text-amber-600" />;
      case 'detection_latency':
        return <Zap className="w-4 h-4 text-blue-600" />;
      case 'source_attribution':
        return <Link2 className="w-4 h-4 text-emerald-600" />;
      default:
        return <ShieldCheck className="w-4 h-4 text-emerald-600" />;
    }
  };

  const getMetricSubtitle = (id: string, actual: string) => {
    switch (id) {
      case 'signal_recall':
        return 'External ground-truth audit required';
      case 'signal_precision':
        return 'Relevancy filtering pending feedback loop';
      case 'monitoring_time':
        return 'Active review session timer required';
      case 'detection_latency':
        return 'published_date vs created_at in DB';
      case 'source_attribution':
        return 'Count of signals with vs without source';
      default:
        return actual;
    }
  };

  const renderStatusBadge = (status: string, label: string) => {
    if (status === 'not_available') {
      return (
        <span className="inline-flex items-center text-[10px] font-semibold text-amber-800 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200 shrink-0">
          {label || 'Telemetry Pending'}
        </span>
      );
    }
    if (status === 'needs_attention') {
      return (
        <span className="inline-flex items-center text-[10px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200/60 shrink-0">
          {label || 'Live Telemetry'}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200/60 shrink-0">
        {label || 'Target Met'}
      </span>
    );
  };

  const renderMetricsList = () => (
    <div className="divide-y divide-slate-100 rounded-lg border border-slate-200/80 overflow-hidden bg-white">
      {metrics.map((kpi: KpiMetricItem) => (
        <button
          key={kpi.id}
          onClick={() => setSelectedKpi(kpi)}
          className="w-full px-4 py-2.5 sm:py-3 hover:bg-slate-50/80 transition text-left cursor-pointer group flex items-center justify-between gap-3 focus:outline-none focus:bg-slate-50"
          title={`Click to view ${kpi.name} data breakdown and audit chart`}
        >
          {/* Left: Icon, Name, Inline Goal, Micro-note */}
          <div className="min-w-0 flex-1 flex flex-col sm:flex-row sm:items-center sm:gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-slate-400 group-hover:text-blue-600 transition-colors shrink-0">
                {getMetricIcon(kpi.id)}
              </span>
              <span className="text-xs sm:text-sm font-semibold text-slate-800 group-hover:text-blue-600 transition-colors truncate">
                {kpi.name}
              </span>
              <span className="hidden md:inline-flex text-[11px] text-slate-400 font-normal">
                Goal: {kpi.target}
              </span>
            </div>
            <span className="text-[11px] text-slate-400 truncate pl-6 sm:pl-0 sm:border-l sm:border-slate-200 sm:pl-3">
              {getMetricSubtitle(kpi.id, kpi.actual)}
            </span>
          </div>

          {/* Right: Value, Status Badge, Arrow */}
          <div className="flex items-center gap-2.5 sm:gap-3 shrink-0">
            <span
              className={`text-xs sm:text-sm font-medium ${
                kpi.status === 'not_available'
                  ? 'text-slate-400 italic'
                  : 'text-slate-900 font-semibold'
              }`}
            >
              {kpi.actual}
            </span>
            {renderStatusBadge(kpi.status, kpi.statusLabel)}
            <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-600 group-hover:translate-x-0.5 transition-all shrink-0" />
          </div>
        </button>
      ))}
    </div>
  );

  if (variant === 'standalone') {
    return (
      <>
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-2xs p-4 sm:p-5 space-y-3.5">
          {/* Streamlined Single-Row Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2.5 flex-wrap min-w-0">
              <div className="w-6 h-6 rounded-md bg-emerald-50 border border-emerald-200/60 flex items-center justify-center text-emerald-700 shrink-0">
                <ShieldCheck className="w-3.5 h-3.5" />
              </div>
              <h2 className="text-sm sm:text-base font-semibold text-slate-900 tracking-tight truncate">
                Executive Success Metrics & Service Level Objectives
              </h2>
              <span
                className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${
                  summary?.totalTracked !== undefined
                    ? 'bg-amber-50 text-amber-900 border border-amber-200/80'
                    : 'bg-emerald-50 text-emerald-800 border border-emerald-200/60'
                }`}
              >
                {summary?.totalTracked !== undefined ? (
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                ) : (
                  <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                )}
                {summary
                  ? summary.totalTracked !== undefined
                    ? `${summary.targetsMet} of ${summary.totalTracked} Tracked Met • ${summary.telemetryPending || 0} Telemetry Pending`
                    : `${summary.targetsMet} of ${summary.totalKpis} Targets Met`
                  : 'Telemetry Pending'}
              </span>
            </div>

            <div className="flex items-center gap-2.5 shrink-0 text-slate-400">
              {summary?.evaluatedAt && (
                <span className="text-[11px] font-mono">
                  {new Date(summary.evaluatedAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              )}
              <button
                onClick={fetchKpis}
                disabled={loading}
                className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition cursor-pointer disabled:opacity-50"
                title="Recalculate live KPI metrics"
                aria-label="Recalculate live KPI metrics"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Loading / Error States */}
          {loading && !data ? (
            <div className="py-8 text-center text-xs text-slate-500 flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />
              <span>Calculating real-time KPI metrics...</span>
            </div>
          ) : error && !data ? (
            <div className="py-3 px-4 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : (
            /* Metric List View */
            renderMetricsList()
          )}
        </div>

        {/* KPI Audit & Statistical Breakdown Modal */}
        {selectedKpi && <KpiDetailModal kpi={selectedKpi} onClose={() => setSelectedKpi(null)} />}
      </>
    );
  }

  return (
    <>
      <div className="border-t border-slate-200/80 bg-slate-50/60 transition-all">
        {/* Header Bar */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle Executive KPI Scorecard"
          className="w-full px-6 py-2.5 flex items-center justify-between bg-slate-50/60 hover:bg-slate-100/70 transition cursor-pointer text-left focus:outline-none group"
          aria-expanded={isOpen}
          title="Toggle Executive KPI Scorecard"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-6 h-6 rounded-md bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-700 shrink-0">
              <ShieldCheck className="w-3.5 h-3.5" />
            </div>
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <span className="font-semibold text-xs text-slate-800 tracking-tight">
                Live Operational Telemetry & Compliance
              </span>
              <span
                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                  summary?.totalTracked !== undefined
                    ? 'bg-amber-50 text-amber-900 border border-amber-200/80'
                    : 'bg-emerald-50 text-emerald-800 border border-emerald-200/60'
                }`}
              >
                {summary?.totalTracked !== undefined ? (
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                ) : (
                  <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                )}
                {summary
                  ? summary.totalTracked !== undefined
                    ? `${summary.targetsMet} of ${summary.totalTracked} Tracked Met • ${summary.telemetryPending || 0} Telemetry Pending`
                    : `${summary.targetsMet} of ${summary.totalKpis} Targets Met`
                  : 'Telemetry Pending'}
              </span>
              <span className="hidden md:inline-flex text-[11px] text-slate-500 font-sans">
                • Verified Live Database Records
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 ml-3">
            <span className="text-[11px] text-slate-600 font-medium hidden sm:inline group-hover:text-slate-900 transition">
              {isOpen ? 'Hide Scorecard' : 'View Performance Scorecard'}
            </span>
            <div className="w-5 h-5 rounded bg-white border border-slate-200 flex items-center justify-center text-slate-500 shadow-2xs group-hover:border-slate-300">
              {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </div>
          </div>
        </button>

        {/* Expanded Scorecard Area */}
        {isOpen && (
          <div className="p-4 sm:p-5 bg-slate-50/60 border-t border-slate-200/70 space-y-3 animate-fade-in">
            {/* Subheader Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
              <div className="text-slate-600">
                <span className="font-medium text-slate-800">Operational Breakdown</span>
                <span className="hidden sm:inline text-slate-500">
                  {' '}
                  — Click any metric to inspect full dataset breakdowns and audit requirements.
                </span>
              </div>

              <div className="flex items-center gap-3">
                {summary?.evaluatedAt && (
                  <span className="text-[11px] text-slate-500 font-mono hidden sm:inline">
                    Updated:{' '}
                    {new Date(summary.evaluatedAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                )}
                <button
                  onClick={fetchKpis}
                  disabled={loading}
                  className="text-[11px] text-slate-600 hover:text-slate-900 bg-white hover:bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-md flex items-center gap-1.5 transition cursor-pointer disabled:opacity-50 shadow-2xs"
                  title="Recalculate live KPI metrics"
                >
                  <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                  <span>Recalculate</span>
                </button>
              </div>
            </div>

            {/* Loading / Error States */}
            {loading && !data ? (
              <div className="py-8 text-center text-xs text-slate-500 flex items-center justify-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />
                <span>Calculating real-time KPI metrics...</span>
              </div>
            ) : error && !data ? (
              <div className="py-3 px-4 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </div>
            ) : (
              /* Metric List View */
              renderMetricsList()
            )}
          </div>
        )}
      </div>

      {/* KPI Audit & Statistical Breakdown Modal */}
      {selectedKpi && <KpiDetailModal kpi={selectedKpi} onClose={() => setSelectedKpi(null)} />}
    </>
  );
};
